import * as Sentry from "@sentry/browser";
import React from "react";

import { APIError } from "../data/api";
import { Button } from "./button";
import { Container } from "./container";
import { Content } from "./content";
import { Row } from "./row";
import { Spacing } from "./spacing";
import { Text } from "./text";

interface ErrorBoundaryState {
  hasError: boolean;
  eventId: string;
}

export class ErrorBoundary extends React.Component<{}, ErrorBoundaryState> {
  constructor(props: {}) {
    super(props);
    this.state = { hasError: false, eventId: "" };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (error instanceof APIError) {
      console.error(error.message);
    } else {
      Sentry.withScope((scope) => {
        scope.setExtras({ errorInfo });
        const eventId = Sentry.captureException(error);
        this.setState({ eventId });
      });
    }
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <Content>
          <Container>
            <Spacing height={80} />
            <Text fontSize="lg" align="center">
              Something went wrong :(
            </Text>
            <Spacing height={8} />
            <Text align="center">
              This crash has been reported to us and we will work on resolving
              the issue as quickly as possible.
            </Text>
            <Spacing height={24} />
            <Row justifyContent="center">
              <Button
                title="Report feedback"
                appearance="outline"
                onPress={() => {
                  Sentry.showReportDialog({ eventId: this.state.eventId });
                }}
              />
            </Row>
          </Container>
        </Content>
      );
    }

    return this.props.children;
  }
}
